import React, { useCallback } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';

import { queryClient } from 'api';
import { formatDistrolistMember } from 'utils/format';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import LoadingSpinner from 'components/common/loaders/LoadingSpinner';
import { Button } from '@epoch-ui/components/Button';
import { Icon } from '@epoch-ui/components/Icon';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription
} from '@epoch-ui/components/Modal/modal';
import { Separator } from '@epoch-ui/components/Separator';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { communityInvitesApi } from '@event-editor/api/event-invites';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { useCanAccessCommunities, useCommunitiesLabel } from 'hooks/useCanAccessCommunities';

const COMMUNITY_INVITES_DESCRIPTION =
  'Members were invited when you sent the invite. People who join a community later are not invited. Removing a community does not remove guests who are already part of the event.';

const ManageCommunityInvitesModal: React.FC<React.ComponentProps<typeof Modal>> = ({
  open,
  onOpenChange,
  ...props
}) => {
  const event = useEditableEvent();
  const canAccessCommunities = useCanAccessCommunities();
  const communitiesLabel = useCommunitiesLabel();
  const { showToast } = useToaster();

  const communityInvitesQuery = useQuery({
    queryKey: eventEditorQueryKeys.eventCommunityInvites(event.id!),
    queryFn: () => communityInvitesApi.list(event.id!),
    enabled: !!open && canAccessCommunities && !!event.id,
    throwOnError: false
  });

  const deleteCommunityInviteMutation = useMutation({
    mutationFn: (communityId: number) => communityInvitesApi.delete(event.id!, communityId),
    onSuccess: (ok) => {
      queryClient.invalidateQueries({
        queryKey: eventEditorQueryKeys.eventCommunityInvites(event.id!)
      });
      // The event carries communityInviteeIds, which drives the "Already in guest list" badge.
      queryClient.invalidateQueries({
        queryKey: eventEditorQueryKeys.event(event.id!),
        exact: true
      });
      if (ok) {
        showToast('Community removed');
      } else {
        showToast('Failed to remove community', { variant: ToasterVariant.ERROR });
      }
    },
    onError: () => {
      showToast('Failed to remove community', { variant: ToasterVariant.ERROR });
    }
  });
  const currentRemovingCommunityId = deleteCommunityInviteMutation.variables;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (deleteCommunityInviteMutation.isPending) {
        return;
      }

      onOpenChange?.(nextOpen);
    },
    [deleteCommunityInviteMutation.isPending, onOpenChange]
  );

  const communities = communityInvitesQuery.data ?? [];

  return (
    <Modal open={open} onOpenChange={handleOpenChange} {...props}>
      <ModalContent size="lg">
        <ModalHeader>
          <ModalTitle>{communitiesLabel}</ModalTitle>
          <ModalDescription>{COMMUNITY_INVITES_DESCRIPTION}</ModalDescription>
        </ModalHeader>

        <Separator className="tw-mb-4" />

        <div className="tw-max-h-[50vh] tw-flex-1 tw-overflow-y-auto tw-py-4">
          {communityInvitesQuery.isLoading && <LoadingSpinner />}
          {!communityInvitesQuery.isLoading && communities.length === 0 ? (
            <p className="tw-m-0 tw-text-center tw-text-sm tw-text-primary-grey">
              No communities found
            </p>
          ) : (
            <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-divide-y tw-divide-border tw-p-0">
              {communities.map((community, index) => (
                <React.Fragment key={community.id}>
                  <li className="tw-flex tw-items-center tw-gap-2">
                    <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-1">
                      <p className="tw-m-0 tw-leading-4">{community.name}</p>
                      <p className="tw-m-0 tw-leading-4">
                        {formatDistrolistMember(community.memberCount)}
                      </p>
                    </div>
                    {!event.hasEnded && (
                      <Button
                        variant="destructive"
                        size="xs"
                        disabled={deleteCommunityInviteMutation.isPending}
                        onClick={() => deleteCommunityInviteMutation.mutate(community.id)}
                        loading={
                          deleteCommunityInviteMutation.isPending &&
                          currentRemovingCommunityId === community.id
                        }
                      >
                        <Icon name="circle-x" size={16} className="tw-mr-2" />
                        Remove
                      </Button>
                    )}
                  </li>

                  {index < communities.length - 1 && <Separator className="tw-my-5" />}
                </React.Fragment>
              ))}
            </ol>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

export { ManageCommunityInvitesModal };
